import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slack, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SlackCard() {
  const [state, setState] = useState({ connected: false, webhook_url_masked: "", last_alert_at: null, last_alert_ok: null });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/slack");
      setState(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings/slack", { webhook_url: webhookUrl.trim() });
      toast.success(webhookUrl.trim() ? "Slack alerts on" : "Slack alerts paused");
      setWebhookUrl("");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/settings/slack/test");
      toast.success("Test alert sent to Slack — check your channel");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setTesting(false); }
  };

  return (
    <div className="ns-card p-8" data-testid="slack-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <Slack className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="ns-label">Low-star alerts</div>
          <div className="font-serif text-2xl">Slack</div>
        </div>
        {state.connected && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-emerald-50 text-emerald-800 border border-emerald-100">
            <ShieldCheck className="w-3.5 h-3.5" /> Wired up
          </span>
        )}
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-4">
        Get pinged the moment a 1-2 star post-stay survey lands, so you can call the guest
        the same afternoon and turn a scathing public review into a private conversation.
        Grab an incoming-webhook URL from a Slack app and paste it below.
      </p>

      <form onSubmit={save} className="grid gap-4 max-w-xl">
        <div>
          <Label className="ns-label">Slack webhook URL</Label>
          <Input
            data-testid="slack-webhook-input"
            className="mt-2 h-11 bg-white font-mono text-xs"
            placeholder={state.webhook_url_masked || "https://hooks.slack.com/services/T…/B…/…"}
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
          />
        </div>
        <div>
          <Button type="submit" disabled={busy} data-testid="slack-save-btn" className="rounded-full ns-btn-primary h-11 px-6">
            {busy ? "Saving…" : state.connected ? "Update webhook" : "Save"}
          </Button>
          {state.connected && (
            <Button
              type="button"
              onClick={test}
              disabled={testing}
              variant="outline"
              data-testid="slack-test-btn"
              className="rounded-full h-11 px-5 ml-2"
            >
              <Send className="w-4 h-4 mr-2" />
              {testing ? "Sending…" : "Send test alert"}
            </Button>
          )}
        </div>
        {state.last_alert_at && (
          <div className={`text-xs ${state.last_alert_ok ? "text-emerald-700" : "text-red-600"}`}>
            Last alert: {state.last_alert_ok ? "Delivered" : "Failed"} · {new Date(state.last_alert_at).toLocaleString()}
          </div>
        )}
        <p className="text-[11px] text-slate-400">
          Ratings of 3+ never send to Slack. A fallback email always goes to the owner regardless of Slack status.
        </p>
      </form>
    </div>
  );
}
